import { inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { patchState, signalStore, type } from '@ngrx/signals';
import { entityConfig, setEntity, withEntities } from '@ngrx/signals/entities';
import {
  withCallStatus,
  withCalls,
  withEntitiesLoadingCall,
  withEntitiesLocalPagination,
  withEntitiesSingleSelection,
  withRouteParams,
} from '@ngrx-traits/signals';
import { filter, first, map, switchMap, tap } from 'rxjs';
import {
  CreateShieldZoneRequest,
  ShieldZoneResponse,
  ShieldZoneService,
  UpdateShieldZoneRequest,
  WafMappedEnum,
  WafMappedEnumList,
  WAFService,
} from '@bn/app-shield';

const config = entityConfig({
  entity: type<ShieldZoneResponse>(),
  selectId: (entity: ShieldZoneResponse) => entity.pullZoneId ?? '', // 1:1 mapping => pull zone id is used here
});

export const AppShieldByPullZoneStore = signalStore(
  { providedIn: 'root' },
  withDevtools('Shield.PullZone'),
  withRouteParams(({ pullZoneId }) => ({
    pullZoneId: Number(pullZoneId),
  })),
  withEntities(config),
  withCallStatus({ ...config, initialValue: 'loading' }),
  withEntitiesLocalPagination({ ...config, pageSize: 100 }),
  withEntitiesSingleSelection(config),
  withEntitiesLoadingCall({
    fetchEntities: ({ pullZoneId }) => {
      const shieldZoneApiService = inject(ShieldZoneService);
      return toObservable(pullZoneId).pipe(
        takeUntilDestroyed(),
        filter((pullZoneId) => !!pullZoneId), // Limit to first full result, so api won't fail
        first(),
        switchMap((pullZoneId) => shieldZoneApiService.getShieldZoneConfigurationByPullZone({ pullZoneId }).pipe(map((res) => (res ? [res.data] : [])))),
      );
    },
  }),
  withCalls((store, shieldZoneApiService = inject(ShieldZoneService), wafApiService = inject(WAFService)) => ({
    loadShieldZoneDetail: ({ id }: { id: number }) => shieldZoneApiService.getShieldZoneConfiguration({ shieldZoneId: id }),
    loadShieldZoneSaveCardA: ({ id }: { id: number }) => shieldZoneApiService.getShieldZoneConfiguration({ shieldZoneId: id }),
    loadShieldZoneByPullZoneDetail: ({ pullZoneId }: { pullZoneId: number }) =>
      shieldZoneApiService.getShieldZoneConfigurationByPullZone({ pullZoneId }).pipe(
        tap((shieldZone) => {
          patchState(
            store,
            setEntity(
              {
                ...shieldZone.data,
              },
              config,
            ),
          );
        }),
      ),
    loadShieldWafEnums: () =>
      wafApiService
        .getWAFEnumMappings()
        .pipe(map(({ data }) => <WafEnums>Object.fromEntries((data ?? []).map(({ enumName, enumValues }: WafMappedEnumList) => [enumName, enumValues])))),
    createShieldZone: ({ pullZoneId, shieldZone }: CreateShieldZoneRequest) =>
      shieldZoneApiService.createShieldZone({ createShieldZoneRequest: { pullZoneId, shieldZone } }).pipe(
        map((res) => {
          const shieldZone = <ShieldZoneResponse>{
            pullZoneId: res.data?.pullZoneId ?? pullZoneId,
            ...(res.data?.shieldZone ?? {}),
          };
          patchState(store, setEntity(shieldZone, config));

          return shieldZone;
        }),
      ),
    updateShieldZone: (updateDto: UpdateShieldZoneRequest) => shieldZoneApiService.updateShieldZone({ updateShieldZoneRequest: updateDto }),
  })),
  // 👆 Adds signals `is[methodCamelCase]Loading()`, `[methodCamelCase]`Result() and method `[methodCamelCase]`({...params})
);

type WafEnums = {
  [key: string]: WafMappedEnum[];
};
